import {
  createSettingsParams,
  SettingsParamType,
  ISettingsParam,
} from '@wix/tpa-settings';
import { DataType } from '@wix/editor-platform-ooi-metadata-resolver';

export type SocialButtonType = 'google' | 'facebook' | 'sso';

interface TextsSettingsParams {
  formTitleText: SettingsParamType.Text;
  formSubtitleText: SettingsParamType.Text;
  formSwitchBarPrefixText: SettingsParamType.Text;
  formSwitchBarTextButtonText: SettingsParamType.Text;
  emailLabelText: SettingsParamType.Text;
  emailPlaceholderText: SettingsParamType.Text;
  passwordLabelText: SettingsParamType.Text;
  passwordPlaceholderText: SettingsParamType.Text;
  resetPasswordLinkText: SettingsParamType.Text;
  submitButtonText: SettingsParamType.Text;
  dividerText: SettingsParamType.Text;
  socialButtonsPrefixText: SettingsParamType.Text;
  mandatorySSOSubtitle: SettingsParamType.Text;
}

interface SectionVisibilitySettingsParams {
  showRecaptcha: SettingsParamType.Boolean;
  showSSOSocialButton: SettingsParamType.Boolean;
  socialButtonsOrder: SocialButtonType[];
}

interface LogoSettingsParams {
  logoUrl: SettingsParamType.Text;
  logoAltText: SettingsParamType.Text;
}

export interface LoginSettingsParams
  extends TextsSettingsParams,
    SectionVisibilitySettingsParams,
    LogoSettingsParams {}

export const withoutDefaultFallback = (
  settingsParam: ISettingsParam<any, any>,
) => ({
  ...settingsParam,
  getDefaultValue: () => '',
});

export default createSettingsParams<LoginSettingsParams>({
  logoUrl: {
    type: SettingsParamType.Text,
    getDefaultValue: () => '',
    metadata: {
      type: DataType.image,
      defaultValue: {
        type: 'static',
        source: '',
      },
      dataItemManifestData: {
        displayName: 'Logo Image',
      },
      ai: {
        description:
          'User-uploaded logo image displayed at the top of the login form',
      },
    },
  },
  logoAltText: {
    type: SettingsParamType.Text,
    getDefaultValue: ({ t }) =>
      t('memberAuthentication.uouScreens.loginForm.logoAlt'),
    metadata: ({ t }) => ({
      type: DataType.text,
      defaultValue: {
        type: 'static',
        value: t('memberAuthentication.uouScreens.loginForm.logoAlt'),
      },
      dataItemManifestData: {
        displayName: 'Logo Alt Text',
      },
      ai: {
        description: 'Accessibility alt text for the logo image',
      },
    }),
  },
  formTitleText: {
    type: SettingsParamType.Text,
    getDefaultValue: ({ t }) =>
      t('memberAuthentication.uouScreens.loginForm.title'),
    metadata: ({ t }) => ({
      type: DataType.text,
      defaultValue: {
        type: 'static' as const,
        value: t('memberAuthentication.uouScreens.loginForm.title'),
      },
      dataItemManifestData: {
        displayName: 'Title',
      },
      ai: {
        description: 'Main heading text of the login form',
      },
    }),
  },
  formSubtitleText: {
    type: SettingsParamType.Text,
    getDefaultValue: ({ t }) =>
      t('memberAuthentication.uouScreens.loginForm.subtitle'),
    metadata: ({ t }) => ({
      type: DataType.text,
      defaultValue: {
        type: 'static',
        value: t('memberAuthentication.uouScreens.loginForm.subtitle'),
      },
      dataItemManifestData: {
        displayName: 'Subtitle',
      },
      ai: {
        description: 'Secondary descriptive text displayed below the title',
      },
    }),
  },
  mandatorySSOSubtitle: {
    type: SettingsParamType.Text,
    getDefaultValue: ({ t }) =>
      t('memberAuthentication.uouScreens.loginForm.mandatorySSOSubtitle'),
    metadata: ({ t }) => ({
      type: DataType.text,
      defaultValue: {
        type: 'static',
        value: t(
          'memberAuthentication.uouScreens.loginForm.mandatorySSOSubtitle',
        ),
      },
      ai: {
        description:
          'Subtitle shown when SSO is the only login method available',
      },
    }),
  },
  formSwitchBarPrefixText: {
    type: SettingsParamType.Text,
    getDefaultValue: ({ t }) =>
      t('memberAuthentication.uouScreens.loginForm.signupPrompt'),
    metadata: ({ t }) => ({
      type: DataType.text,
      defaultValue: {
        type: 'static',
        value: t('memberAuthentication.uouScreens.loginForm.signupPrompt'),
      },
      ai: {
        description:
          'Prompt text before the signup link, e.g. "Don\'t have an account?"',
      },
    }),
  },
  formSwitchBarTextButtonText: {
    type: SettingsParamType.Text,
    getDefaultValue: ({ t }) =>
      t('memberAuthentication.uouScreens.loginForm.signupLink'),
    metadata: ({ t }) => ({
      type: DataType.text,
      defaultValue: {
        type: 'static',
        value: t('memberAuthentication.uouScreens.loginForm.signupLink'),
      },
      ai: {
        description:
          'Clickable link text to switch to the signup form, e.g. "Sign Up"',
      },
    }),
  },
  emailLabelText: {
    type: SettingsParamType.Text,
    getDefaultValue: ({ t }) =>
      t('memberAuthentication.uouScreens.loginForm.emailLabel'),
    metadata: ({ t }) => ({
      type: DataType.text,
      defaultValue: {
        type: 'static',
        value: t('memberAuthentication.uouScreens.loginForm.emailLabel'),
      },
      ai: {
        description: 'Label above the email input field',
      },
    }),
  },
  emailPlaceholderText: {
    type: SettingsParamType.Text,
    getDefaultValue: () => '',
    metadata: {
      type: DataType.text,
      defaultValue: {
        type: 'static',
        value: '',
      },
      ai: {
        description: 'Placeholder text inside the email input field',
      },
    },
  },
  passwordLabelText: {
    type: SettingsParamType.Text,
    getDefaultValue: ({ t }) =>
      t('memberAuthentication.uouScreens.loginForm.passwordLabel'),
    metadata: ({ t }) => ({
      type: DataType.text,
      defaultValue: {
        type: 'static',
        value: t('memberAuthentication.uouScreens.loginForm.passwordLabel'),
      },
      ai: {
        description: 'Label above the password input field',
      },
    }),
  },
  passwordPlaceholderText: {
    type: SettingsParamType.Text,
    getDefaultValue: () => '',
    metadata: {
      type: DataType.text,
      defaultValue: {
        type: 'static',
        value: '',
      },
      ai: {
        description: 'Placeholder text inside the password input field',
      },
    },
  },
  resetPasswordLinkText: {
    type: SettingsParamType.Text,
    getDefaultValue: ({ t }) =>
      t('memberAuthentication.uouScreens.loginForm.forgotPassword'),
    metadata: ({ t }) => ({
      type: DataType.text,
      defaultValue: {
        type: 'static',
        value: t('memberAuthentication.uouScreens.loginForm.forgotPassword'),
      },
      ai: {
        description:
          'Text for the forgot password link below the password field',
      },
    }),
  },
  submitButtonText: {
    type: SettingsParamType.Text,
    getDefaultValue: ({ t }) =>
      t('memberAuthentication.uouScreens.loginForm.loginButton'),
    metadata: ({ t }) => ({
      type: DataType.text,
      defaultValue: {
        type: 'static',
        value: t('memberAuthentication.uouScreens.loginForm.loginButton'),
      },
      ai: {
        description: 'Label on the login submit button',
      },
    }),
  },
  dividerText: {
    type: SettingsParamType.Text,
    getDefaultValue: ({ t }) =>
      t('memberAuthentication.uouScreens.loginForm.orDivider'),
    metadata: ({ t }) => ({
      type: DataType.text,
      defaultValue: {
        type: 'static',
        value: t('memberAuthentication.uouScreens.loginForm.orDivider'),
      },
      ai: {
        description:
          'Text in the divider between the email form and social login options',
      },
    }),
  },
  socialButtonsPrefixText: {
    type: SettingsParamType.Text,
    getDefaultValue: () => 'continueWith',
    metadata: {
      type: DataType.textEnum,
      defaultValue: {
        type: 'static',
        value: 'continueWith',
        options: [
          { value: 'continueWith', displayName: 'Continue with' },
          { value: 'logInWith', displayName: 'Log in with' },
        ],
      },
      ai: {
        description:
          'Text prefix pattern for social login buttons. The selected key maps to a translated label like "Continue with Google".',
      },
    },
  },
  showRecaptcha: {
    type: SettingsParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: DataType.booleanValue,
      defaultValue: { type: 'static', value: true },
      ai: {
        description:
          'Whether to display reCAPTCHA bot verification below the form',
      },
    },
  },
  showSSOSocialButton: {
    type: SettingsParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: DataType.booleanValue,
      defaultValue: { type: 'static', value: true },
      ai: {
        description: 'Whether to show the SSO social login button',
      },
    },
  },
  socialButtonsOrder: {
    type: SettingsParamType.Object,
    getDefaultValue: () => ['google', 'facebook', 'sso'],
    metadata: {
      type: DataType.schema,
      defaultValue: {
        type: 'static',
        value: JSON.stringify({
          type: 'array',
          items: { type: 'string', enum: ['google', 'facebook', 'sso'] },
          uniqueItems: true,
        }),
      },
      ai: {
        description:
          'Ordered list of social login providers controlling display order. Values: google, facebook, sso.',
      },
    },
  },
});
